import * as React from 'react'
import { pdfjs } from 'react-pdf'
import { Document, Page } from 'react-pdf'
import { useTranslation } from 'react-i18next'
import { EmptySheet, Sheet } from '@components'
import userApi from '@user/api'
import type { InvoicesDetailProps } from './InvoiceDetail.types'
import { useNotification } from '@core/hooks'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

const InvoicesDetail: React.FC<InvoicesDetailProps> = ({ id, invoiceNo }) => {
    const { t } = useTranslation()
    const { toast } = useNotification()

    const observerRef = React.useRef<ResizeObserver | null>(null)

    const [numPages, setNumPages] = React.useState<number>(0)
    const [pageRatio, setPageRatio] = React.useState<number>(0)
    const [containerSize, setContainerSize] = React.useState<{
        width: number
        height: number
    }>({ width: 0, height: 0 })
    const [renderError, setRenderError] = React.useState<boolean>(false)

    const invoicePDFQuery = userApi.useInvoicePDFQuery(
        { id: id },
        { refetchOnMountOrArgChange: true },
    )

    const file = React.useMemo(() => {
        if (!invoicePDFQuery.data) return null

        return URL.createObjectURL(
            new Blob([invoicePDFQuery.data], { type: 'application/pdf' }),
        )
    }, [invoicePDFQuery.data])

    React.useEffect(() => {
        return () => {
            if (file) URL.revokeObjectURL(file)
        }
    }, [file])

    const isError = React.useMemo(
        () => invoicePDFQuery.isError || renderError,
        [invoicePDFQuery.isError, renderError],
    )

    const fitsByHeight = React.useMemo(() => {
        if (!pageRatio || !containerSize.height) return false

        return containerSize.width / containerSize.height > pageRatio
    }, [pageRatio, containerSize])

    const handleLoadSuccess = React.useCallback(
        async (pdf: pdfjs.PDFDocumentProxy) => {
            const page = await pdf.getPage(1)
            const viewport = page.getViewport({ scale: 1 })

            setPageRatio(viewport.width / viewport.height)
            setNumPages(pdf.numPages)
        },
        [],
    )

    const handleLoadError = React.useCallback(
        () => {
            setRenderError(true)

            toast({
                variant: 'error',
                message: t('@user.InvoiceDetail.pdf_toast_error_message'),
                duration: 5000,
            })
        },
        // eslint-disable-next-line
        [t],
    )

    const containerRef = React.useCallback((element: HTMLDivElement | null) => {
        observerRef.current?.disconnect()
        observerRef.current = null

        if (!element) return

        const observer = new ResizeObserver(([entry]) => {
            if (!entry) return

            setContainerSize({
                width: entry.contentRect.width,
                height: entry.contentRect.height,
            })
        })

        observer.observe(element)
        observerRef.current = observer
    }, [])

    return (
        <React.Fragment>
            <Sheet.Content
                loading={(!numPages && !isError) || invoicePDFQuery.isFetching}>
                <Sheet.Header>
                    <Sheet.Title>
                        {t('@user.InvoiceDetail.route_header_title')}
                    </Sheet.Title>
                    <Sheet.Description className="sr-only">
                        {invoiceNo}
                    </Sheet.Description>
                </Sheet.Header>
                {!isError && (
                    <div
                        ref={containerRef}
                        className="-mx-6 flex flex-col items-center justify-center overflow-hidden sm:-mx-10"
                        style={{ height: 'calc(100% - 40px)' }}>
                        {file && !invoicePDFQuery.isFetching ? (
                            <Document
                                file={file}
                                onLoadSuccess={handleLoadSuccess}
                                onLoadError={handleLoadError}
                                loading={null}
                                error={null}>
                                {containerSize.width > 0 &&
                                    Array.from(
                                        { length: numPages },
                                        (_, index) => (
                                            <Page
                                                key={`page_${index + 1}`}
                                                pageNumber={index + 1}
                                                width={
                                                    fitsByHeight
                                                        ? undefined
                                                        : containerSize.width
                                                }
                                                height={
                                                    fitsByHeight
                                                        ? containerSize.height
                                                        : undefined
                                                }
                                                loading={null}
                                            />
                                        ),
                                    )}
                            </Document>
                        ) : null}
                    </div>
                )}
                {isError && (
                    <EmptySheet
                        icon="warning"
                        title={t('@user.InvoiceDetail.error_title')}
                        description={t('@user.InvoiceDetail.error_description')}
                    />
                )}
            </Sheet.Content>
        </React.Fragment>
    )
}

export default InvoicesDetail
